import { Map, useMap } from "@vis.gl/react-google-maps";
import { MapPinOff } from "lucide-react";
import { useEffect, useRef } from "react";

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "components/ui/empty";
import { AddressMarkers } from "components/CaseMap/AddressMarkers";
import { CaseMapLegend } from "components/CaseMap/CaseMapLegend";
import { EventMarkers } from "components/CaseMap/EventMarkers";
import { MachineMarkers } from "components/CaseMap/MachineMarkers";
import { ProximityRings } from "components/ProximityRings";
import useCaseMapStore from "stores/useCaseMapStore";

const MAP_ID = "49c70c2f29feecc1b45bc70f";

function MapContents({
  geoJson,
  bbox,
  canEditStatus,
  events,
  addresses,
  caseName,
  proximityRings,
}) {
  const map = useMap();
  const fittedBbox = useRef(null);
  const proximityRingsVisible = useCaseMapStore(s => s.proximityRingsVisible);

  // The machines query hands back a fresh array on every refetch — a status
  // change invalidates it — so frame the map only when the extent itself moved.
  // Reacting to the array's identity would yank the camera back and throw away
  // whatever the analyst had panned to.
  useEffect(() => {
    if (!map || !bbox) return;

    const extent = bbox.join();
    if (fittedBbox.current === extent) return;
    fittedBbox.current = extent;

    const [west, south, east, north] = bbox;
    map.fitBounds({ west, south, east, north });
  }, [map, bbox]);

  return (
    <>
      <ProximityRings
        geoJson={proximityRings}
        visible={proximityRingsVisible}
      />
      <AddressMarkers addresses={addresses} caseName={caseName} />
      <EventMarkers geojson={events.geoJson} />
      <MachineMarkers geojson={geoJson} canEditStatus={canEditStatus} />
    </>
  );
}

export function CaseMapCanvas({
  geoJson,
  bbox,
  emptyMessage,
  canEditStatus,
  events,
  addresses,
  caseName,
  proximityRings,
}) {
  // A case whose machines are all unplottable still has somewhere to be shown
  // if it carries an address, so the empty panel is only for an empty map.
  if (emptyMessage && !addresses.length) {
    return (
      <div className="tw:flex tw:h-full tw:items-center tw:justify-center tw:bg-(--color-background)">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <MapPinOff />
            </EmptyMedia>
            <EmptyTitle>No locations to show</EmptyTitle>
            <EmptyDescription>{emptyMessage}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className="tw:relative tw:size-full">
      <Map
        defaultZoom={3}
        defaultCenter={{ lat: 22.54992, lng: 0 }}
        gestureHandling="greedy"
        mapId={MAP_ID}
        className="tw:size-full"
      >
        <MapContents
          geoJson={geoJson}
          bbox={bbox}
          canEditStatus={canEditStatus}
          events={events}
          addresses={addresses}
          caseName={caseName}
          proximityRings={proximityRings}
        />
      </Map>
      <CaseMapLegend />
    </div>
  );
}
